import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { useRibbonAndExport } from 'src/hooks/useRibbonAndExport';
import { PageLayout } from 'src/layouts';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetScheduleRegister } from '@/hooks/queries/scheduler/useGetScheduleRegister';
import { useGetScheduleTaskTypes } from '@/hooks/queries/scheduler/useGetScheduleTaskTypes';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';

import { useGetCollectionTableProps } from './config';
import ScheduleModal from './ScheduleModal';
import { SchedulerRegisterToolbar } from './SchedulerRegisterToolbar';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page: FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'scheduler' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const { data, isLoading } = useGetScheduleRegister();
  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const openCreate = useCallback(() => setIsModalVisible(true), []);

  // The server answers the whole create rule (see `useGetScheduleTaskTypes`).
  // An in-flight read has no data, so the button stays hidden rather than
  // flashing on.
  const { data: schedulableTaskTypes } = useGetScheduleTaskTypes();
  const canCreate = (schedulableTaskTypes?.length ?? 0) > 0;

  const tableProps = useGetCollectionTableProps(data);
  const title = st('register_title');
  const counter = getCounter(tableProps.totalItemsCount, isLoading);

  return (
    <PageLayout
      title={title}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <SchedulerRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            onCreate={canCreate ? openCreate : undefined}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
          {canCreate && (
            <Button variant={'primary'} onClick={openCreate}>
              {st('create_new_button')}
            </Button>
          )}
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={isLoading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        showEditButton={false}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={isLoading} />
      {/* Unmounted rather than hidden when closed, so each open starts a fresh form. */}
      {isModalVisible && (
        <ScheduleModal onDismiss={() => setIsModalVisible(false)} />
      )}
    </PageLayout>
  );
};

export default Page;
